import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { HelperService } from 'src/app/services/helper/helper.service';

export type PaginationData<T> = {
  items: T[];
  setFirst?: boolean;
};

@Component({
  selector: 'app-pagination-list',
  templateUrl: './pagination-list.component.html',
  styleUrls: ['./pagination-list.component.scss'],
})
export class PaginationListComponent implements OnDestroy {
  @Output()
  public emitPageChange = new EventEmitter<any[]>();

  @ViewChild('paginator') paginator: MatPaginator;

  public isLoading$: Observable<boolean>;

  public items$: Observable<any[]>;

  public pagedItems$: Observable<any[]>;

  public pageIndex = 0;
  public pageSize = 20;

  public showEmptySubject = new BehaviorSubject<boolean>(true);

  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  private itemsSubject = new BehaviorSubject<any[]>([]);
  private pagedItemsSubject = new BehaviorSubject<any[]>([]);

  private subscriptions: Subscription[] = [];

  constructor(private helperService: HelperService) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.items$ = this.itemsSubject.asObservable();
    this.pagedItems$ = this.pagedItemsSubject.asObservable();
  }

  // Helper function
  public get helper() {
    return this.helperService;
  }

  @Input()
  set _showEmpty(_showEmpty: boolean) {
    if (_showEmpty !== undefined) {
      this.showEmptySubject.next(_showEmpty);
    }
  }

  @Input()
  set _isLoading(_isLoading: boolean) {
    if (_isLoading !== undefined) {
      this.isLoadingSubject.next(_isLoading);
    }
  }

  @Input()
  set _data(_data: PaginationData<any>) {
    if (_data !== undefined) {
      if (Array.isArray(_data.items)) {
        this.itemsSubject.next(_data.items);
        if (this.paginator) {
          const setFirst = !!_data.setFirst;
          if (setFirst) {
            this.paginator.firstPage();
          }
          this.pageIndex = this.paginator.pageIndex;
          this.pageSize = this.paginator.pageSize;
          this.helper.setPagination(
            this.paginator,
            this.itemsSubject,
            this.pagedItemsSubject,
            setFirst,
            this.pageIndex,
            this.pageSize,
          );
        }
      }
    }
  }

  @Input()
  set _items(_items: any[]) {
    if (_items) {
      this.itemsSubject.next(_items);
      if (this.paginator) {
        this.paginator.firstPage();
        this.pageIndex = this.paginator.pageIndex;
        this.pageSize = this.paginator.pageSize;
        this.helper.setPagination(
          this.paginator,
          this.itemsSubject,
          this.pagedItemsSubject,
          true,
          this.pageIndex,
          this.pageSize,
        );
      }
    }
  }

  public onPageChange(event: PageEvent): void {
    const startIndex = event.pageIndex * event.pageSize;
    const endIndex = Math.min(startIndex + event.pageSize, this.itemsSubject.value.length);
    this.pagedItemsSubject.next(this.itemsSubject.value.slice(startIndex, endIndex));
    this.emitPageChange.emit(this.itemsSubject.value.slice(startIndex, endIndex));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
